import React, { useState } from 'react';
import { moneyShort, money } from './format';

/**
 * Small bar chart: one series of bars (series-1) with an optional target
 * tick per bar (series-2). Handles negative values with a zero baseline.
 * Hover shows a tooltip; a table view is always available.
 */
export default function BarChart({ title, rows, valueLabel, targetLabel }) {
  const [hover, setHover] = useState(null);
  const [asTable, setAsTable] = useState(false);
  const hasTarget = rows.some(r => typeof r.target === 'number');
  const values = rows.flatMap(r => [r.value, hasTarget ? r.target : null]).filter(v => typeof v === 'number');
  const max = Math.max(0, ...values);
  const min = Math.min(0, ...values);
  const span = max - min || 1;
  const zeroPct = (max / span) * 100; // distance of the zero line from the top
  const pos = (v) => ((max - v) / span) * 100;

  return (
    <div className="viz">
      <div className="viz-head">
        <div className="viz-title">{title}</div>
        <button className="hc-toggle" onClick={() => setAsTable(t => !t)}>{asTable ? 'Show chart' : 'Show table'}</button>
      </div>
      {hasTarget && !asTable && (
        <div className="viz-legend">
          <span><i className="viz-key viz-key-bar" />{valueLabel}</span>
          <span><i className="viz-key viz-key-target" />{targetLabel}</span>
        </div>
      )}
      {asTable ? (
        <table className="viz-table">
          <thead><tr><th>Month</th><th>{valueLabel}</th>{hasTarget && <th>{targetLabel}</th>}</tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.label}><td>{r.label}</td><td>{money(r.value)}</td>{hasTarget && <td>{money(r.target)}</td>}</tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="viz-plot">
          <div className="viz-axis">
            <span>{moneyShort(max)}</span>
            {min < 0 && <span style={{ top: `${zeroPct}%` }} className="viz-axis-zero">£0</span>}
            <span className="viz-axis-bottom">{moneyShort(min)}</span>
          </div>
          <div className="viz-bars" onMouseLeave={() => setHover(null)}>
            <div className="viz-zero" style={{ top: `${zeroPct}%` }} />
            {rows.map((r, i) => {
              const top = r.value >= 0 ? pos(r.value) : zeroPct;
              const height = Math.abs(r.value) / span * 100;
              return (
                <div key={r.label} className="viz-col" onMouseEnter={() => setHover(i)}>
                  <div className={`viz-bar ${r.value < 0 ? 'neg' : ''}`} style={{ top: `${top}%`, height: `${height}%` }} />
                  {typeof r.target === 'number' && <div className="viz-target" style={{ top: `${pos(r.target)}%` }} />}
                  {hover === i && (
                    <div className={`viz-tip ${i > rows.length / 2 ? 'left' : ''}`}>
                      <strong>{r.label}</strong>
                      <div>{valueLabel}: {money(r.value)}</div>
                      {typeof r.target === 'number' && <div>{targetLabel}: {money(r.target)}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="viz-labels">
            {rows.map((r, i) => <span key={r.label}>{i % 2 === 0 || rows.length <= 6 ? r.label : ''}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}
